import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney, CURRENCY_META } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Wallet, CreditCard, Landmark, ShieldCheck, Loader2, CheckCircle2, Lock } from "lucide-react";

const METHODS = [
  { id: "ob_balance", labelKey: "co.obBalance", icon: Wallet, enabled: true },
  { id: "obcard", labelKey: "co.obcard", icon: CreditCard, enabled: true },
  { id: "external_card", labelKey: "co.bankCard", icon: Landmark, enabled: true },
  { id: "apple", labelKey: "co.applePay", icon: CreditCard, enabled: false },
  { id: "google", labelKey: "co.googlePay", icon: CreditCard, enabled: false },
];

export default function Checkout() {
  const { id } = useParams();
  const { t } = useI18n();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("obcard");
  const [accounts, setAccounts] = useState([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // card fields
  const [cardNo, setCardNo] = useState("");
  const [mm, setMm] = useState("");
  const [yy, setYy] = useState("");
  const [cvv, setCvv] = useState("");
  const [accountId, setAccountId] = useState("");

  useEffect(() => {
    api.get(`/payments/${id}`)
      .then((r) => { setPayment(r.data); if (r.data.status === "paid") setDone(true); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    if (localStorage.getItem("ob_token")) {
      api.get("/accounts").then((r) => { setAccounts(r.data.filter((a) => a.status !== "closed")); setLoggedIn(true); }).catch(() => {});
    }
  }, [id]);

  const pay = async () => {
    setBusy(true);
    try {
      const body = { method };
      if (method === "ob_balance") body.account_id = accountId;
      if (method === "obcard" || method === "external_card") {
        body.card_number = cardNo.replace(/\s/g, "");
        body.expiry_month = parseInt(mm, 10);
        body.expiry_year = parseInt(yy, 10);
        body.cvv = cvv;
        if (method === "external_card") body.brand = "VISA";
      }
      const { data } = await api.post(`/payments/${id}/pay`, body);
      setDone(true);
      toast.success(t("co.success"));
      if (data.redirect_url) setTimeout(() => { window.location.href = data.redirect_url; }, 1500);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black"><div className="w-48 ob-loader h-1 rounded-full bg-zinc-900" /></div>;
  if (!payment) return <div className="min-h-screen flex items-center justify-center bg-black text-zinc-400">404</div>;

  const dec = 2;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <Logo size="md" />
          <LanguageToggle />
        </div>

        <div className="ob-card rounded-lg p-7">
          {done ? (
            <div className="text-center py-8" data-testid="checkout-success">
              <CheckCircle2 size={56} className="mx-auto text-emerald-400 mb-4" />
              <h2 className="text-2xl font-bold text-white">{t("co.paid")}</h2>
              <p className="text-zinc-500 text-sm mt-2">{payment.merchant_name}</p>
              <div className="font-num text-3xl font-extrabold text-white mt-4" dir="ltr">{fmtMoney(payment.amount, dec)} {payment.currency}</div>
            </div>
          ) : (
            <>
              <div className="text-center pb-5 border-b border-zinc-900">
                <div className="text-xs text-zinc-500">{t("co.payTo")}</div>
                <div className="text-lg font-bold text-white">{payment.merchant_name}</div>
                <div className="text-xs text-zinc-500 mt-2">{t("co.amountDue")}</div>
                <div className="font-num text-4xl font-extrabold text-white mt-1" dir="ltr" data-testid="checkout-amount">{fmtMoney(payment.amount, dec)} <span className="text-xl text-[#00E5FF]">{payment.currency}</span></div>
                {payment.description && <div className="text-xs text-zinc-500 mt-2">{payment.description}</div>}
              </div>

              <div className="py-5">
                <div className="text-xs font-semibold tracking-widest text-zinc-400 uppercase mb-3">{t("co.method")}</div>
                <div className="grid grid-cols-1 gap-2">
                  {METHODS.map((m) => (
                    <button key={m.id} disabled={!m.enabled} onClick={() => setMethod(m.id)} data-testid={`method-${m.id}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md border text-sm transition-all ${method === m.id ? "border-[#00E5FF] bg-[#00E5FF]/10 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"} ${!m.enabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <m.icon size={18} /> {t(m.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {method === "ob_balance" && (
                <div className="mb-5">
                  {loggedIn ? (
                    <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="checkout-account-select"
                      className="w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] outline-none">
                      <option value="">{t("co.selectAccount")}</option>
                      {accounts.map((a) => <option key={a.id} value={a.id}>{`${CURRENCY_META[a.currency_code]?.flag || ""} ${a.account_number} · ${fmtMoney(a.available_balance, 2)}`}</option>)}
                    </select>
                  ) : (
                    <a href="/login" className="block text-center text-sm text-[#00E5FF] hover:underline">{t("co.loginHint")}</a>
                  )}
                </div>
              )}

              {(method === "obcard" || method === "external_card") && (
                <div className="space-y-3 mb-5">
                  {method === "external_card" && <div className="text-[11px] text-amber-400 bg-amber-500/10 rounded px-3 py-2">{t("co.sandbox")}</div>}
                  <input value={cardNo} onChange={(e) => setCardNo(e.target.value)} dir="ltr" placeholder={t("co.cardNumber")} data-testid="checkout-card-number"
                    className="w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white font-mono-ob text-end focus:border-[#00E5FF] outline-none" />
                  <div className="grid grid-cols-3 gap-3">
                    <input value={mm} onChange={(e) => setMm(e.target.value)} dir="ltr" placeholder={t("co.month")} data-testid="checkout-mm"
                      className="bg-[#121212] border border-zinc-800 rounded-md px-3 py-3 text-white text-center focus:border-[#00E5FF] outline-none" />
                    <input value={yy} onChange={(e) => setYy(e.target.value)} dir="ltr" placeholder={t("co.year")} data-testid="checkout-yy"
                      className="bg-[#121212] border border-zinc-800 rounded-md px-3 py-3 text-white text-center focus:border-[#00E5FF] outline-none" />
                    <input value={cvv} onChange={(e) => setCvv(e.target.value)} dir="ltr" placeholder={t("cards.cvv")} data-testid="checkout-cvv"
                      className="bg-[#121212] border border-zinc-800 rounded-md px-3 py-3 text-white text-center focus:border-[#00E5FF] outline-none" />
                  </div>
                </div>
              )}

              <button onClick={pay} disabled={busy} data-testid="checkout-pay-button"
                className="w-full bg-[#00E5FF] text-black font-bold py-3.5 rounded-md hover:bg-[#33EBFF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} {t("co.payNow")}
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-1.5 mt-5 text-xs text-zinc-600">
          <ShieldCheck size={13} /> {t("co.securedBy")} <span className="font-num font-bold text-zinc-400">OB PAY</span>
        </div>
      </div>
    </div>
  );
}
